/**
 * Chamadas às actions da app a partir do iframe.
 *
 * O iframe nunca fala directamente com as rotas do host: passa sempre por uma
 * action (`POST /api/apps/<key>/actions/<verb>`), que o host despacha para o
 * handler remoto com um token HMAC. É esse salto que permite ao host aplicar
 * o `min_role` por action — esconder ecrãs no cliente é cosmético, o token
 * viaja no URL do iframe.
 */

import type { AppClient } from "@koortex/apps-sdk"

interface ActionEnvelope<T> {
  success: boolean
  data?: T
  error?: string
}

export class ActionError extends Error {}

async function invoke<T>(
  client: AppClient,
  verb: string,
  params: Record<string, unknown> = {},
): Promise<T> {
  const res = await client.post<ActionEnvelope<T>>(`actions/${verb}`, { params })
  if (!res?.success || res.data === undefined) {
    throw new ActionError(res?.error ?? `A ação ${verb} falhou.`)
  }
  return res.data
}

// ── Radar ───────────────────────────────────────────────────────────────────

export interface RadarGate {
  id: string
  label: string | null
  sequence: number
  status: string
  expected_at: string | null
  delivered_at: string | null
  amount: number | null
}

export interface RadarIssue {
  reference: string
  title: string | null
  assignee: string | null
  spent_hours: number
  estimated_hours: number
  over: boolean
  url: string | null
}

export interface CandidateSignal {
  key: string
  label: string
  severity: "info" | "warn" | "bad"
}

export interface RadarOverlay {
  id?: string
  project_reference?: string
  focus_pinned_until?: string | null
  muted_until?: string | null
  strategic_importance?: string | null
  blocker_text?: string | null
  waiting_on?: string | null
  goal_id?: string | null
  reviewed_at?: string | null
}

export interface RadarProject {
  reference: string
  project_name: string | null
  client_name: string | null
  client_entity_id: number | null
  owner_name: string | null
  contract_id: string | null
  current_phase: string | null
  phase_days: number | null
  bucket: "active" | "waiting" | "dormant"
  health: "on_track" | "at_risk" | "delayed"
  logged_hours: number
  estimated_hours: number
  last_activity_days: number | null
  proposal_value: number | null
  value_bucket: "S" | "M" | "L" | null
  euro_per_hour: number | null
  next_milestone: RadarGate | null
  gates: RadarGate[]
  delivered_unbilled_amount: number | null
  open_issues_count: number
  closed_this_week: number
  issues: RadarIssue[]
  candidate_signals: CandidateSignal[]
  overlay: RadarOverlay | null
  is_focus: boolean
  is_muted: boolean
}

export interface RadarResponse {
  projects: RadarProject[]
  muted_count: number
  focus_count: number
  totals: {
    active: number
    waiting: number
    dormant: number
    waiting_amount: number | null
    delivered_unbilled_amount: number | null
    at_risk: number
  }
  thresholds: { activeDays: number; milestoneHorizonDays: number; dormantDays: number }
  can_see_money: boolean
}

export const loadRadar = (client: AppClient, bucket?: string) =>
  invoke<RadarResponse>(client, "radar_list", bucket ? { bucket } : {})

export const upsertOverlay = (client: AppClient, params: Record<string, unknown>) =>
  invoke<{ overlay: RadarOverlay }>(client, "radar_upsert_overlay", params)

export const markDelivered = (
  client: AppClient,
  contractId: string,
  milestoneId: string,
  delivered = true,
) =>
  invoke<{ milestone: unknown }>(client, "mark_milestone_delivered", {
    contract_id: contractId,
    milestone_id: milestoneId,
    delivered,
  })

export const planMilestone = (client: AppClient, params: Record<string, unknown>) =>
  invoke<{ milestone: unknown }>(client, "plan_milestone", params)

// ── O Que Resolvemos ────────────────────────────────────────────────────────

export interface Achievement {
  id: string
  title: string
  description: string | null
  occurred_on: string
  level: "win" | "big_win" | "game_changer"
  weight: number
  result_types: string[]
  problem_tag: string | null
  owner_name: string | null
  contributors: string[]
  entity_id: number | null
  project_reference: string | null
  goal_id: string | null
  revenue: number | null
  reusable: boolean
  time_saved_minutes: number | null
  time_saved_confidence: string | null
  source_kind: string
}

export interface AchievementsResponse {
  achievements: Achievement[]
  totals: {
    count: number
    clients: number
    revenue: number
    reusable: number
    time_saved_estimated_minutes: number
    time_saved_measured_minutes: number
    weight_total: number
    weight_by_type: Record<string, number>
  }
}

export const loadAchievements = (client: AppClient, from: string, to: string) =>
  invoke<AchievementsResponse>(client, "achievements_list", { from, to })

export interface WinSuggestion {
  key: string
  kind: "milestone_delivered" | "deal_won" | "contract_completed"
  occurred_at: string
  title: string
  client_name: string | null
  client_entity_id: number | null
  project_reference: string | null
  contract_id: string | null
  milestone_id: string | null
  amount: number | null
  suggested_result_types: string[]
  evidence: string[]
}

export const loadSuggestions = (client: AppClient, sinceDays = 30) =>
  invoke<{ suggestions: WinSuggestion[]; decided_count: number }>(
    client,
    "win_suggestions_list",
    { since_days: sinceDays },
  )

export const registerWin = (client: AppClient, params: Record<string, unknown>) =>
  invoke<{ achievement: Achievement; weight: number; level: string }>(
    client,
    "register_win",
    params,
  )

export const decideSuggestion = (
  client: AppClient,
  sourceKey: string,
  decision: "registered" | "ignored",
  achievement?: Record<string, unknown>,
) =>
  invoke<{ decision: unknown; achievement: unknown }>(client, "decide_suggestion", {
    source_key: sourceKey,
    decision,
    achievement,
  })

// ── Objetivos ───────────────────────────────────────────────────────────────

/**
 * Forma **real** da resposta de `/api/apps/<key>/goals` — lida da rota, não
 * inferida. A rota devolve `parent.title AS objective_title` e o array em
 * `data`, não em `goals`; assumir o contrário dava board vazio sem erro.
 */
export interface GoalKr {
  id: string
  title: string
  description: string | null
  level: string
  parent_id: string | null
  objective_title: string | null
  target_value: number | null
  current_value: number | null
  unit: string | null
  period: string | null
  data_source: string | null
  status: string
  progress_pct: number | null
  latest_checkin: { confidence: string; checked_at: string; note?: string } | null
}

/**
 * Rota de plataforma, gated pela permissão `goals.read` do manifest.
 *
 * `scope=team` é obrigatório aqui: o default do endpoint devolve só os KRs de
 * que o utilizador é dono (foi desenhado para a app RH, "os meus objetivos").
 * Este board é da equipa — sem o scope, mostra vazio a toda a gente.
 */
export const loadGoals = (client: AppClient) =>
  client
    .get<{ success: boolean; data?: GoalKr[] }>("goals?scope=team")
    .then((r) => r.data ?? [])

// ── Ciclos por fechar ───────────────────────────────────────────────────────

export type OpenCycleState = "ready" | "running" | "blocked"

export interface OpenCycle {
  contract_id: string
  contract_title: string | null
  entity_id: number | null
  entity_name: string | null
  service_level: string | null
  cycle_months: number | null
  window_from: string
  window_to: string
  /** Negativo quando a janela já terminou. */
  days_to_close: number
  state: OpenCycleState
  blockers: string[]
  hours_consumed: number
  hours_included: number | null
  billable_hours: number
  effective_hourly_rate: number | null
  estimated_amount: number | null
}

export interface OpenCyclesResponse {
  cycles: OpenCycle[]
  totals: {
    ready: number
    blocked: number
    running: number
    /** Contratos com ciclo aberto mas sem horas — fora da lista por omissão. */
    empty: number
    ready_amount: number
  }
}

export const loadOpenCycles = (client: AppClient, includeEmpty = false) =>
  invoke<OpenCyclesResponse>(client, "open_cycles_list", { include_empty: includeEmpty })

export const closeCycle = (
  client: AppClient,
  contractId: string,
  from: string,
  to: string,
) => invoke<{ cycle: unknown }>(client, "close_cycle", { contract_id: contractId, from, to })
